"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface SEOContentContextType {
	seoContent: ReactNode | null;
	setSeoContent: (content: ReactNode | null) => void;
}

// Create context with a default value to improve type safety
const SEOContentContext = createContext<SEOContentContextType>({
	seoContent: null,
	setSeoContent: () => {
		throw new Error("useSEOContent must be used within a SEOContentProvider");
	},
});

export function SEOContentProvider({ children }: { children: ReactNode }) {
	const [seoContent, setSeoContentState] = useState<ReactNode | null>(null);

	// Memoize setSeoContent to prevent unnecessary re-renders
	const setSeoContent = useCallback((content: ReactNode | null) => {
		setSeoContentState(content);
	}, []);

	// Memoize context value to prevent unnecessary re-renders
	const value = useMemo(
		() => ({
			seoContent,
			setSeoContent,
		}),
		[seoContent, setSeoContent],
	);

	return <SEOContentContext.Provider value={value}>{children}</SEOContentContext.Provider>;
}

export function useSEOContent() {
	const context = useContext(SEOContentContext);
	// With default value, this check is now redundant but kept for defensive programming
	if (!context) {
		throw new Error("useSEOContent must be used within a SEOContentProvider");
	}
	return context;
}
